import React from 'react';
import { graphql, navigate } from 'gatsby';
import { GatsbyImage, getImage, withArtDirection } from 'gatsby-plugin-image';
import styled, { css } from 'styled-components';
import {
  brandColours,
  breakpointSizes,
  fontSize,
  fontWeights,
  minBreakpointQuery,
  standardColours,
} from '../styles';
import { Link } from './ui';
import { buildUrl, getCheapestProductPrice } from '../utils';

const StyledProductCategoryCard = styled.article`
  position: relative;
  display: flex;
  flex-direction: column;
  padding: 30px;
  background-color: ${standardColours.white};
  border-radius: 15px;
  cursor: pointer;
`;

const StyledImageWrapper = styled.div`
  margin: auto;
  padding: 20px 10px;
  max-width: 100%;

  ${minBreakpointQuery.small`
    padding: 25px 15px;
  `}
`;

const StyledImage = styled(GatsbyImage)`
  ${({ hasFeaturedLargeImage }) => {
    if (hasFeaturedLargeImage) {
      return css`
        ${minBreakpointQuery.xlarge`
          width: 600px;
          max-width: 100%;
        `}
      `;
    }
  }}
`;

const StyledFromPrice = styled.p`
  position: absolute;
  top: 10px;
  right: 10px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 10px;
  height: 60px;
  width: 60px;
  color: ${standardColours.white};
  ${fontSize(14)};
  line-height: 1.15;
  text-align: center;
  background-color: ${brandColours.secondary};
  border-radius: 50%;

  ${minBreakpointQuery.small`
    top: 15px;
    right: 15px;
    height: 70px;
    width: 70px;
    ${fontSize(16)};
  `}

  ${minBreakpointQuery.large`
    top: 20px;
    right: 20px;
    height: 80px;
    width: 80px;
    ${fontSize(18)};
  `}

  span {
    ${fontSize(16)};
    letter-spacing: 0.5px;

    ${minBreakpointQuery.small`
      ${fontSize(18)};
    `}

    ${minBreakpointQuery.large`
      ${fontSize(20)};
    `}
  }
`;

const StyledHeading = styled.h3`
  font-weight: ${fontWeights.medium};
  ${fontSize(18)};
  text-align: center;

  ${minBreakpointQuery.large`
    ${fontSize(20)};
  `}
`;

const ProductCategoryCard = data => {
  const {
    image,
    largeImage,
    heading,
    slug,
    shopifyCollection,
    isFeatured,
    isFirstInListing,
    ...props
  } = data;
  const hasFeaturedLargeImage = isFeatured && largeImage;

  const images =
    image &&
    hasFeaturedLargeImage &&
    withArtDirection(getImage(image), [
      {
        media: `(min-width: ${breakpointSizes.xlarge}px)`,
        image: getImage(largeImage),
      },
    ]);

  return (
    <StyledProductCategoryCard
      onClick={() => navigate(buildUrl(slug, data))}
      {...props}
    >
      {image && (
        <StyledImageWrapper>
          <StyledImage
            image={images || image.gatsbyImageData}
            alt={image.alt}
            hasFeaturedLargeImage={hasFeaturedLargeImage}
            {...(isFirstInListing && {
              loading: 'eager',
              fetchPriority: 'high',
            })}
          />
          {shopifyCollection && shopifyCollection.products.length > 0 && (
            <StyledFromPrice>
              From{' '}
              <span>
                {getCheapestProductPrice(shopifyCollection.products, true)}
              </span>
            </StyledFromPrice>
          )}
        </StyledImageWrapper>
      )}
      <StyledHeading>
        <Link to={data}>{heading}</Link>
      </StyledHeading>
    </StyledProductCategoryCard>
  );
};

export default ProductCategoryCard;

export const ProductCategoryCardFragment = graphql`
  fragment ProductCategoryCardFragment on DatoCmsProductCategory {
    heading: title
    image: featuredImage {
      gatsbyImageData(width: 300, imgixParams: { w: "300" })
      alt
    }
    ...LinkFragment
  }
`;
